import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import App from '../AppExports'; // Import only from AppExports
import Header from '../components/Header';
import { eventService, invitationService, templateService } from '../services/db'; 
import { useAuth } from '../contexts/AuthContext';
import PageTitle from '../components/PageTitle';
import './InvitationEditorPage.css';
import { captureFullPageScreenshot } from '../utils/thumbnailGenerator';

console.log("InvitationEditorPage is importing App:", typeof App);

// Save notification component - reused from EditorPage
const SaveNotification = ({ isVisible, progress, status }) => {
  if (!isVisible) return null;
  
  return (
    <div className={`save-notification ${status}`}>
      <div className="save-notification-content">
        {status === 'saving' && (
          <>
            <div className="save-spinner"></div>
            <span>Saving invitation...</span>
            {progress < 100 && (
              <div className="save-progress-container">
                <div className="save-progress-bar" style={{ width: `${progress}%` }}></div>
              </div>
            )}
          </>
        )}
        
        {status === 'success' && (
          <>
            <svg className="check-icon" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
            <span>Invitation saved successfully</span>
          </>
        )}
        
        {status === 'error' && (
          <>
            <svg className="error-icon" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <span>Error saving invitation</span>
          </>
        )}
      </div>
    </div>
  );
};

function InvitationEditorPage() {
  // Get URL parameters and navigation
  const { eventId, invitationId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Define ALL hooks at the top level (React requirement)
  const [event, setEvent] = useState(null);
  const [invitationName, setInvitationName] = useState('New Invitation');
  const [elements, setElements] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [error, setError] = useState('');
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  // Keep this state for other potential validation messages
  const [validationMessage, setValidationMessage] = useState('');
  // Add a state variable to track the template ID
  const [templateId, setTemplateId] = useState(null);
  // Add this state variable at the top with other state declarations
  const [createdTemplateId, setCreatedTemplateId] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ visible: false, progress: 0, status: 'idle' });
  
  // Add this effect to load the created template ID from localStorage
  useEffect(() => {
    // First check URL for template ID
    const urlParams = new URLSearchParams(window.location.search);
    const fromTemplateId = urlParams.get('fromTemplateId');
    if (fromTemplateId) {
      console.log("Received template ID from URL:", fromTemplateId);
      setTemplateId(fromTemplateId);
    }

    // Then check localStorage for previously created template ID
    const storedTemplateId = localStorage.getItem(`invitation_template_${invitationId}`);
    if (storedTemplateId) {
      console.log("Found stored template ID:", storedTemplateId);
      setCreatedTemplateId(storedTemplateId);
    }
  }, [invitationId]);
  
  // Add this function to create URL-friendly slugs
  const createSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };
  
  // We've removed the validateInvitationName function since we're not checking for duplicate names anymore
  
  // Then, update the handleSaveInvitation function to skip validation for updates
  const handleSaveInvitation = async (publish = false) => {
    try {
      // Show saving notification
      setSaveStatus({ visible: true, progress: 10, status: 'saving' });
      
      // We removed the name validation check here
      // Clear any validation messages 
      setValidationMessage('');
      
      console.log("🔍 SAVING INVITATION: Starting save process");
      console.log("🔍 Current user:", currentUser);
      console.log("🔍 Event ID:", eventId, "Type:", typeof eventId);
      console.log("🔍 Invitation ID:", invitationId, "Type:", typeof invitationId);
      
      // IMPORTANT: Sync elements before saving to ensure we have the latest changes
      const latestElements = syncElementsBeforeSave();
      
      // Small delay to ensure state updates after sync
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Update progress
      setSaveStatus({ visible: true, progress: 20, status: 'saving' });
      
      // Use the synced elements or current state
      let elementsCopy = JSON.parse(JSON.stringify(latestElements || elements));
      
      console.log("🔍 Elements count for save:", elementsCopy.length);
      
      // If elements exist, log details of first element to debug content changes
      if (elementsCopy.length > 0) {
        const firstEl = elementsCopy[0];
        console.log("First element:", { 
          id: firstEl.id, 
          type: firstEl.type,
          content: firstEl.type === 'text' ? firstEl.content : '(not text)'
        });
      }
      
      // Enable preview mode to generate thumbnail
      console.log("🔍 Enabling preview mode to generate thumbnail");
      setIsPreviewMode(true);
      
      // Capture screenshot for thumbnail
      console.log("📥 [InvitationEditorPage] Attempting to capture canvas screenshot for template");
      let screenshot = null;
      
      try {
        // Try to capture the screenshot
        screenshot = await captureFullPageScreenshot();
        setSaveStatus({ visible: true, progress: 35, status: 'saving' });
        
        if (screenshot) {
          console.log("✅ [InvitationEditorPage] Canvas screenshot captured successfully, length:", screenshot.length);
        } else {
          console.error("❌ [InvitationEditorPage] Failed to capture canvas screenshot");
        }
      } catch (screenshotError) {
        console.error("❌ [InvitationEditorPage] Error capturing canvas screenshot:", screenshotError);
      }
      
      console.log("🔍 Saving invitation for event:", eventId, "Type:", typeof eventId);
      
      // Create invitation with string eventId
      const invitation = {
        id: invitationId === 'new' ? null : invitationId, // Don't set ID for new invitations
        eventId: String(eventId), // Make sure eventId is a string
        name: invitationName.trim() || `Invitation for ${event?.name || 'Event'}`,
        elements: elementsCopy,
        published: publish || isPublished
      };
      
      console.log("🔍 Invitation data prepared:", {
        id: invitation.id,
        eventId: invitation.eventId,
        name: invitation.name,
        elementsCount: invitation.elements.length,
        published: invitation.published
      });
      
      // Update progress
      setSaveStatus({ visible: true, progress: 50, status: 'saving' });
      
      // Determine if this is a create or update operation
      let updatedInvitation;
      if (invitationId === 'new') {
        console.log("🔍 Creating NEW invitation");
        updatedInvitation = await invitationService.create(invitation);
        console.log("🔍 Invitation CREATED with ID:", updatedInvitation.id);
      } else {
        console.log("🔍 Updating EXISTING invitation with ID:", invitationId);
        updatedInvitation = await invitationService.update(invitationId, invitation);
        console.log("🔍 Invitation UPDATED");
      }
      
      // Update progress
      setSaveStatus({ visible: true, progress: 70, status: 'saving' });
      
      // NEW FEATURE: Also save as a template without event ID
      console.log("🔍 Saving as template for future reuse");
      try {
        // Prepare template data (same as invitation but without eventId)
        const templateData = {
          name: invitation.name + " (Template)",
          elements: elementsCopy,
          userId: currentUser.id,
          description: `Template created from invitation "${invitation.name}"`,
          createdFromEventId: String(eventId),
          createdAt: new Date().toISOString()
        };
        
        // Add the screenshot as thumbnail if available
        if (screenshot) {
          console.log("✅ [InvitationEditorPage] Adding screenshot as template thumbnail");
          templateData.thumbnail = screenshot;
        }
        
        console.log("🔍 Template data prepared:", {
          name: templateData.name,
          elementsCount: templateData.elements.length,
          userId: templateData.userId,
          hasThumbnail: !!templateData.thumbnail
        });
        
        // Update progress
        setSaveStatus({ visible: true, progress: 85, status: 'saving' });
        
        // Check localStorage first for existing template ID
        const storedTemplateId = localStorage.getItem(`invitation_template_${invitationId}`);
        let savedTemplate;

        if (storedTemplateId) {
          // First check if the template with this ID exists
          try {
            console.log("🔍 Checking if template exists with ID:", storedTemplateId);
            const existingTemplate = await templateService.getById(storedTemplateId);
            
            if (existingTemplate) {
              // Update existing template
              console.log("🔍 Updating existing template with ID:", storedTemplateId);
              savedTemplate = await templateService.update(storedTemplateId, templateData);
              console.log("🔍 Template UPDATED");
            } else {
              // Template ID was stored but template doesn't exist anymore
              console.log("🔍 Stored template ID doesn't exist, creating new template");
              templateData.createdAt = new Date().toISOString();
              savedTemplate = await templateService.create(templateData);
              console.log("🔍 New template created with ID:", savedTemplate.id);
              
              // Update the stored template ID
              localStorage.setItem(`invitation_template_${invitationId}`, savedTemplate.id);
              setCreatedTemplateId(savedTemplate.id);
            }
          } catch (templateCheckError) {
            console.error("❌ ERROR checking template:", templateCheckError);
            
            // Create a new template since we couldn't access the existing one
            console.log("🔍 Creating new template after ID check failed");
            templateData.createdAt = new Date().toISOString();
            savedTemplate = await templateService.create(templateData);
            console.log("🔍 New template created with ID:", savedTemplate.id);
            
            // Update the stored template ID
            localStorage.setItem(`invitation_template_${invitationId}`, savedTemplate.id);
            setCreatedTemplateId(savedTemplate.id);
          }
        } else {
          // Create new template only if we don't have one stored
          console.log("🔍 Creating NEW template");
          templateData.createdAt = new Date().toISOString();
          savedTemplate = await templateService.create(templateData);
          console.log("🔍 Template CREATED with ID:", savedTemplate.id);
          
          // Store the new template ID in localStorage
          localStorage.setItem(`invitation_template_${invitationId}`, savedTemplate.id);
          setCreatedTemplateId(savedTemplate.id);
        }
        
      } catch (templateErr) {
        // Don't fail the whole operation if template saving fails
        console.error("❌ ERROR saving template:", templateErr);
        setValidationMessage("Your invitation was saved, but we couldn't save it as a template for reuse. You can try again later.");
        setTimeout(() => {
          setValidationMessage('');
        }, 3000);
      }
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      // Update status to success
      setSaveStatus({ visible: true, progress: 100, status: 'success' });
      
      // Show success messages or redirect as needed
      if (!publish) {
        // Hide the success notification after a delay
        setTimeout(() => {
          setSaveStatus({ visible: false, progress: 0, status: 'idle' });
        }, 3000);
      } else {
        // For published invitations, show styled modal instead of alert
        const eventSlug = createSlug(event?.name || 'event');
        const invitationSlug = createSlug(invitationName);
        const baseUrl = window.location.origin;
        
        // Get user ID from the event owner
        const userIdentifier = event?.userId?.substring(0, 5) || currentUser?.id?.substring(0, 5) || 'user';
        
        // Get the uniqueId for the invitation - first 6 chars of invitation ID 
        const invUniqueId = updatedInvitation?.id?.substring(0, 6) || '';
        
        console.log('🔴 URL GENERATION:', {
          eventName: event?.name,
          eventSlug,
          invitationName,
          invitationSlug,
          userId: event?.userId,
          currentUserId: currentUser?.id,
          userIdentifier,
          invitationId: updatedInvitation?.id,
          invUniqueId
        });
        
        // Update URL format to include user identifier and unique invitation ID suffix
        // This ensures URLs are always unique even if names conflict
        const invitationUrl = `${baseUrl}/${userIdentifier}/${eventSlug}/${invitationSlug}-${invUniqueId}`;
        
        console.log('🔴 FINAL URL:', invitationUrl);
        
        // Hide the success notification
        setSaveStatus({ visible: false, progress: 0, status: 'idle' });
        
        setShareUrl(invitationUrl);
        setShowUrlModal(true);
        // Don't navigate yet - navigation happens when modal is closed
      }
    } catch (err) {
      console.error("❌ ERROR saving invitation:", err);
      
      // Show error notification
      setSaveStatus({ visible: true, progress: 100, status: 'error' });
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      setValidationMessage(`Failed to save: ${err.message}`);
      setTimeout(() => {
        setValidationMessage('');
        setSaveStatus({ visible: false, progress: 0, status: 'idle' });
      }, 5000);
    }
  };
  
  // Add this to capture element changes properly
  const handleElementsChange = (updatedElements) => {
    console.log("InvitationEditorPage received element updates, count:", updatedElements.length);
    
    // Log changes to first element to debug text issues
    if (updatedElements.length > 0 && elements.length > 0 && 
        updatedElements[0].id === elements[0].id && 
        updatedElements[0].type === 'text') {
      console.log("First text element change:", {
        before: elements[0].content,
        after: updatedElements[0].content
      });
    }
    
    // Use a deep clone to ensure we break all references
    const elementsCopy = JSON.parse(JSON.stringify(updatedElements));
    
    // Store a copy in localStorage as backup to prevent data loss
    try {
      localStorage.setItem(`invitation_elements_${invitationId}`, JSON.stringify(elementsCopy));
      console.log("Elements backed up to localStorage:", elementsCopy.length);
    } catch (err) {
      console.error("Failed to backup elements to localStorage:", err);
    }
    
    setElements(elementsCopy);
  };
  
  // Add this function to copy the URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        // Show copied confirmation
        const copyButton = document.querySelector('.copy-url-btn');
        if (copyButton) {
          copyButton.textContent = 'Copied!';
          setTimeout(() => {
            copyButton.textContent = 'Copy URL';
          }, 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  // Add this function to ensure we always have the latest elements before saving
  const syncElementsBeforeSave = () => {
    try {
      // Force any active element to blur to capture changes
      if (document.activeElement && document.activeElement.isContentEditable) {
        document.activeElement.blur();
      }
      
      // Check for backed up elements in localStorage
      const backupElements = localStorage.getItem(`invitation_elements_${invitationId}`);
      if (backupElements) {
        const parsedBackup = JSON.parse(backupElements);
        if (parsedBackup && Array.isArray(parsedBackup) && parsedBackup.length > 0) {
          console.log("Syncing with backed up elements before save");
          setElements(parsedBackup);
          return parsedBackup;
        }
      }
      return elements;
    } catch (err) {
      console.error("Error syncing elements before save:", err);
      return elements;
    }
  };
  
  // Use this before/during the publish operation
  useEffect(() => {
    // Add event listener for beforeunload to ensure elements are saved
    const handleBeforeUnload = () => {
      localStorage.setItem(`invitation_elements_${invitationId}`, JSON.stringify(elements));
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [elements, invitationId]);
  
  // Load event and invitation data
  useEffect(() => {
    console.log("🔍 InvitationEditorPage mounted with eventId:", eventId, "invitationId:", invitationId);
    // Skip loading for new invitations - just set defaults and mark as loaded
    if (invitationId === 'new') {
      setInvitationName('New Invitation');
      setElements([]);
      setIsLoaded(true);
      return;
    }
    
    // Only load data for existing invitations
    const loadData = async () => {
      try {
        setIsLoaded(false);
        console.log("Loading invitation for editing, ID:", invitationId);
        
        // Load event first
        const event = await eventService.getById(eventId);
        if (!event) {
          setError("Event not found. Please return to the events page.");
          setIsLoaded(true);
          return;
        }
        setEvent(event);
        
        // Now load the invitation
        console.log("Loading invitation with ID:", invitationId);
        const invitation = await invitationService.getById(invitationId);
        
        if (invitation) {
          console.log("Invitation loaded:", invitation);
          console.log("Elements found:", invitation.elements?.length || 0);
          
          // Set name and make sure it's editable
          setInvitationName(invitation.name || '');
          
          // IMPORTANT: Make sure we properly set elements
          if (invitation.elements && Array.isArray(invitation.elements)) {
            console.log("Setting elements from invitation:", invitation.elements.length);
            setElements(invitation.elements);
          } else {
            console.warn("No elements found in invitation or not an array");
            setElements([]);
          }
          
          setIsPublished(invitation.published || false);
        } else {
          setError("Invitation not found. Please return to the event page.");
        }
      } catch (err) {
        console.error("Error loading invitation:", err);
        setError("Failed to load invitation data.");
      } finally {
        setIsLoaded(true);
      }
    };
    
    loadData();
    return () => console.log("🔍 InvitationEditorPage unmounted");
  }, [eventId, invitationId, navigate]);
  
  // Render loading state
  if (!isLoaded) {
    return <div className="loading">Loading...</div>;
  }
  
  // Check for new invitation - render directly without error checking
  const isNewInvitation = invitationId === 'new';
  
  // Show error only for existing invitations
  if (error && !isNewInvitation) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate(`/events/${eventId}`)}>
          Back
        </button>
      </div>
    );
  }
  
  // Common editor component for both new and existing invitations
  return (
    <div className="invitation-editor">
      <PageTitle title={invitationName || "Invitation Editor"} />
      <div className="editor-header">
        <div className="editor-controls">
          <button className="back-btn" onClick={() => navigate(`/events/${eventId}`)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 8H1M1 8L8 1M1 8L8 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Event
          </button>
          <input
            type="text"
            className="invitation-name-input"
            placeholder="Invitation Name"
            value={invitationName}
            onChange={(e) => setInvitationName(e.target.value)}
          />
        </div>
        <div className="editor-actions">
          <button 
            className="save-draft-btn" 
            onClick={() => handleSaveInvitation(false)}
            disabled={saveStatus.status === 'saving'}
          >
            {saveStatus.status === 'saving' ? 'Saving...' : 'Save Draft'}
          </button>
          <button 
            className="publish-btn" 
            onClick={() => handleSaveInvitation(true)}
            disabled={saveStatus.status === 'saving'}
          >
            {saveStatus.status === 'saving' ? 'Publishing...' : 'Publish Invitation'}
          </button>
        </div>
      </div>
      
      <SaveNotification 
        isVisible={saveStatus.visible}
        progress={saveStatus.progress}
        status={saveStatus.status}
      />
      
      {validationMessage && (
        <div className="validation-message">{validationMessage}</div>
      )}
      
      <div className="editor-container">
        {isLoaded && (
          <App 
            initialElements={elements}
            onElementsChange={handleElementsChange}
            eventId={eventId}
            invitationId={invitationId}
            key={`invitation-${invitationId}`}
            isPreviewMode={isPreviewMode}
          />
        )}
      </div>
      
      {/* Share URL Modal */}
      {showUrlModal && (
        <div className="modal-overlay">
          <div className="modal share-modal">
            <div className="modal-header">
              <h3>Your invitation is published!</h3>
              <button 
                className="modal-close-btn"
                onClick={() => {
                  setShowUrlModal(false);
                  navigate(`/events/${eventId}`);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="success-icon-container">
                <svg className="success-icon" width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#4CAF50" stroke="#4CAF50" strokeWidth="2"/>
                  <path d="M7 12L10 15L17 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <p className="share-message">Share this link with your guests:</p>
              
              <div className="url-container">
                <input 
                  type="text" 
                  value={shareUrl} 
                  readOnly 
                  onClick={(e) => e.target.select()} 
                  className="share-url-input"
                />
                <button className="copy-url-btn" onClick={copyToClipboard}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5H6C4.89543 5 4 5.89543 4 7V19C4 20.1046 4.89543 21 6 21H16C17.1046 21 18 20.1046 18 19V17M8 5C8 6.10457 8.89543 7 10 7H12C13.1046 7 14 6.10457 14 5M8 5C8 3.89543 8.89543 3 10 3H12C13.1046 3 14 3.89543 14 5M14 5H16C17.1046 5 18 5.89543 18 7V10M20 14V16.5C20 17.3284 19.3284 18 18.5 18H13.5C12.6716 18 12 17.3284 12 16.5V11.5C12 10.6716 12.6716 10 13.5 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Copy URL</span>
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="done-btn"
                onClick={() => {
                  setShowUrlModal(false);
                  navigate(`/events/${eventId}`);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvitationEditorPage; 